import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { Button, Typography } from "@mui/material";
import { Drawer, Select, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import type { PlatformFundingRequest, PlatformFundingSource } from "./types";

interface AddFundsDrawerProps {
  open: boolean;
  onClose: () => void;
  sources: PlatformFundingSource[];
  saving: boolean;
  onFund: (input: PlatformFundingRequest) => Promise<boolean>;
}

const sourceLabel = (source: PlatformFundingSource) =>
  `${source.name ?? source.institutionName ?? source.id}${source.last4 ? ` **** ${source.last4}` : ""}`;

const AddFundsDrawer: React.FC<AddFundsDrawerProps> = ({
  open,
  onClose,
  sources,
  saving,
  onFund,
}) => {
  const { t } = useTranslation();
  const [fundingSourceId, setFundingSourceId] = useState("");
  const [amount, setAmount] = useState("");
  // One id per request, so a retry after a lost response is the same debit
  const [requestId, setRequestId] = useState<string>(() => uuidv4());
  const selected = sources.some((source) => source.id === fundingSourceId)
    ? fundingSourceId
    : sources[0]?.id || "";
  const parsedAmount = Number(amount);
  const valid =
    selected !== "" && Number.isFinite(parsedAmount) && parsedAmount > 0;

  useEffect(() => {
    if (open) {
      setRequestId(uuidv4());
      setAmount("");
    }
  }, [open]);

  const handleFund = async () => {
    if (!valid) return;
    const done = await onFund({
      requestId,
      fundingSourceId: selected,
      amount: parsedAmount,
    });
    if (done) onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (saving) return;
        onClose();
      }}
      title={t("Add funds")}
      loading={saving}
      footer={
        <Inline gap="var(--space-3)">
          <Button
            variant="primary-filled"
            onClick={handleFund}
            disabled={saving || !valid}
          >
            {t("Add funds")}
          </Button>
          <Button
            variant="primary-outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("Cancel")}
          </Button>
        </Inline>
      }
    >
      {sources.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t("NoActiveFundingSource")}
        </Typography>
      ) : (
        <Stack gap="var(--space-4)">
          <Typography variant="body2" color="text.secondary">
            {t("AddFundsCaption")}
          </Typography>
          <Select
            label={t("Funding bank")}
            fullWidth
            options={sources.map((source) => ({
              id: source.id,
              label: sourceLabel(source),
            }))}
            value={selected || null}
            onChange={(id) => setFundingSourceId(id ?? "")}
          />
          <TextField
            label={t("Amount")}
            fullWidth
            type="number"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Stack>
      )}
    </Drawer>
  );
};

export default AddFundsDrawer;
